import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { ToastConfig } from '../../shared/ui/toast';
import { TranslateService } from './translate.service';

const DEFAULT_TOAST_DURATION_MS = 5000;
const TOAST_EXIT_DURATION_MS = 300;
const PERSISTENT_TOASTS_KEY = 'manavi_persistent_toasts';
const LEGACY_SYNC_RUNNING_KEY = 'toast_sync_running';

type ToastInput = Omit<ToastConfig, 'id' | 'isRemoving'> & { id?: string };
type ProcessToastInput = Omit<ToastConfig, 'isProcess' | 'isRemoving' | 'duration' | 'persistOnReload'>;
type ProcessResultToastInput = Omit<ToastInput, 'isProcess' | 'persistOnReload'>;

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly toastsSubject = new BehaviorSubject<ToastConfig[]>([]);
  private readonly autoDismissTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly removalTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly resultTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly isBrowser: boolean;

  readonly toasts$: Observable<ToastConfig[]> = this.toastsSubject.asObservable();

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private translate: TranslateService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this.restorePersistentToasts();
      this.restoreLegacyBackgroundSync();
    }
  }

  showToast(config: ToastInput): string {
    const id = config.id || this.createToastId();
    const toast: ToastConfig = {
      ...config,
      id,
      position: config.position || 'top-right',
      tone: config.tone || 'neutral',
      showLeftIcon: config.showLeftIcon !== false,
      showHeader: config.showHeader !== false,
      showMessage: config.showMessage !== false,
      isRemoving: false
    };

    this.clearToastTimers(id);

    const currentToasts = this.toastsSubject.getValue();
    const nextToasts = currentToasts.some(currentToast => currentToast.id === id)
      ? currentToasts.map(currentToast => currentToast.id === id ? toast : currentToast)
      : [...currentToasts, toast];

    this.updateToasts(nextToasts);

    if (!toast.isProcess && toast.duration !== 0) {
      const duration = toast.duration ?? DEFAULT_TOAST_DURATION_MS;
      const timer = setTimeout(() => {
        this.autoDismissTimers.delete(id);
        this.removeToast(id);
      }, duration);
      this.autoDismissTimers.set(id, timer);
    }

    return id;
  }

  showProcessToast(config: ProcessToastInput): string {
    return this.showToast({
      ...config,
      isProcess: true,
      duration: 0,
      persistOnReload: true
    });
  }

  getActiveProcessToasts(): ToastConfig[] {
    return this.toastsSubject.getValue()
      .filter(toast => toast.isProcess && toast.persistOnReload && !toast.isRemoving)
      .map(toast => ({ ...toast }));
  }

  finishProcessToast(id: string, resultToast?: ProcessResultToastInput): void {
    if (!this.hasToast(id)) {
      return;
    }

    this.removeToast(id);

    if (resultToast) {
      const timer = setTimeout(() => {
        this.resultTimers.delete(id);
        this.showToast(resultToast);
      }, TOAST_EXIT_DURATION_MS);
      this.resultTimers.set(id, timer);
    }
  }

  removeToast(id: string): void {
    const currentToasts = this.toastsSubject.getValue();
    const toast = currentToasts.find(currentToast => currentToast.id === id);
    if (!toast || toast.isRemoving) {
      return;
    }

    this.clearTimer(this.autoDismissTimers, id);
    this.updateToasts(
      currentToasts.map(currentToast =>
        currentToast.id === id
          ? { ...currentToast, isRemoving: true }
          : currentToast
      )
    );

    const timer = setTimeout(() => {
      this.removalTimers.delete(id);
      const remainingToasts = this.toastsSubject.getValue().filter(currentToast => currentToast.id !== id);
      this.updateToasts(remainingToasts);
    }, TOAST_EXIT_DURATION_MS);
    this.removalTimers.set(id, timer);
  }

  demoBackgroundSync(): void {
    this.startSyncToast();
  }

  finishBackgroundSync(): void {
    this.finishProcessToast('sync-process', {
      id: 'sync-success',
      title: this.translate.translate('HELPER_TOAST_TITLE'),
      message: this.translate.translate('TOAST_SYNC_SUCCESS'),
      tone: 'success',
      duration: 5000
    });
  }

  private startSyncToast(): void {
    this.showProcessToast({
      id: 'sync-process',
      title: this.translate.translate('HELPER_TOAST_TITLE'),
      message: this.translate.translate('TOAST_SYNC_RUNNING'),
      tone: 'neutral',
      position: 'top-right',
      processId: 'demo-sync'
    });
  }

  private updateToasts(toasts: ToastConfig[]): void {
    this.toastsSubject.next(toasts);
    this.persistToasts(toasts);
  }

  private persistToasts(toasts: ToastConfig[]): void {
    if (!this.isBrowser) {
      return;
    }

    const persistentToasts = toasts.filter(toast => toast.persistOnReload && !toast.isRemoving);

    try {
      if (persistentToasts.length === 0) {
        localStorage.removeItem(PERSISTENT_TOASTS_KEY);
        return;
      }

      localStorage.setItem(PERSISTENT_TOASTS_KEY, JSON.stringify(persistentToasts));
    } catch (error) {
      console.error('Unable to persist toast state', error);
    }
  }

  private restorePersistentToasts(): void {
    try {
      const storedValue = localStorage.getItem(PERSISTENT_TOASTS_KEY);
      if (!storedValue) {
        return;
      }

      const parsedValue: unknown = JSON.parse(storedValue);
      if (!Array.isArray(parsedValue)) {
        localStorage.removeItem(PERSISTENT_TOASTS_KEY);
        return;
      }

      const restoredToasts = parsedValue
        .filter((toast): toast is ToastConfig =>
          !!toast &&
          typeof toast === 'object' &&
          typeof toast.id === 'string' &&
          typeof toast.message === 'string'
        )
        .map(toast => ({
          ...toast,
          position: toast.position || 'top-right',
          tone: toast.tone || 'neutral',
          showLeftIcon: toast.showLeftIcon !== false,
          showHeader: toast.showHeader !== false,
          showMessage: toast.showMessage !== false,
          isRemoving: false
        }));

      this.toastsSubject.next(restoredToasts);
    } catch (error) {
      localStorage.removeItem(PERSISTENT_TOASTS_KEY);
      console.error('Unable to restore toast state', error);
    }
  }

  private restoreLegacyBackgroundSync(): void {
    if (localStorage.getItem(LEGACY_SYNC_RUNNING_KEY) !== 'true') {
      return;
    }

    localStorage.removeItem(LEGACY_SYNC_RUNNING_KEY);
    this.startSyncToast();
  }

  private hasToast(id: string): boolean {
    return this.toastsSubject.getValue().some(toast => toast.id === id && !toast.isRemoving);
  }

  private clearToastTimers(id: string): void {
    this.clearTimer(this.autoDismissTimers, id);
    this.clearTimer(this.removalTimers, id);
    this.clearTimer(this.resultTimers, id);
  }

  private clearTimer(
    timers: Map<string, ReturnType<typeof setTimeout>>,
    id: string
  ): void {
    const timer = timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.delete(id);
    }
  }

  private createToastId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
}
